<template>
  <div>
    <navbar :links="links" />
    <v-layout class="mt-4">
      <span class="grey--text text--darken-2 subheading">{{
        $t('tvtemplatesListPageHint')
      }}</span>
    </v-layout>
    <content-creator-plan-status-hint
      class="mt-2 grey--text text--darken-2 subheading"
    />
    <v-layout
      align-center
      class="mt-1"
      wrap
    >
      <!-- 'No Branding?' Hint -->
      <router-link
        v-show="$store.getters.userCanUseBrandings"
        v-if="!loading && brandings.length === 0"
        :to="{name: 'branding', query: {create: true}}"
        class="subheading mt-1"
      >
        {{ $t('tvtemplatesPleaseCreateBrandingTitle') }}
      </router-link>
      <v-layout
        v-show="!loading"
        justify-center
        align-center
        wrap
        class="mt-2"
      >
        <!-- Branding Widget -->
        <all-tv-templates-branding-widget
          v-if="brandings.length > 0"
          ref="applybrandingwidget"
          class="mr-4"
          :class="{'ml-auto': $vuetify.breakpoint.mdAndUp}"
          :title="lastBrandingApplied ? lastBrandingApplied.name : null"
          :tvtemplates="tvtemplates"
          @apply-branding="onBrandingAppliedToTvTemplate"
          @applied="onAllBrandingsHaveBeenApplied"
        />
        <!-- Filters -->
        <div
          v-if="brandings.length > 0"
          class="d-flex nowrap"
          :class="{'mr-auto': $vuetify.breakpoint.mdAndUp}"
        >
          <v-btn
            icon
            class="mx-1"
            :title="$t('tvtemplatesFilterFavorites')"
            @click="filterFavorites = !filterFavorites"
          >
            <v-icon :color="filterFavorites ? 'msr' : 'grey lighten-1'">
              favorite
            </v-icon>
          </v-btn>
          <v-btn
            icon
            class="mx-1"
            :title="$t('tvtemplatesFilterFormatLandscape')"
            @click="formatFilterLandscape = !formatFilterLandscape"
          >
            <v-icon :color="formatFilterLandscape ? 'msr' : 'grey'">
              crop_landscape
            </v-icon>
          </v-btn>
          <v-btn
            icon
            class="mx-1"
            :title="$t('tvtemplatesFilterFormatPortrait')"
            @click="formatFilterPortrait = !formatFilterPortrait"
          >
            <v-icon :color="formatFilterPortrait ? 'msr' : 'grey'">
              crop_portrait
            </v-icon>
          </v-btn>
        </div>
      </v-layout>
    </v-layout>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <v-slide-y-transition>
      <v-layout
        v-if="!loading && !tvtemplates.length"
        justify-center
      >
        <v-subheader>
          {{ noTvTemplatesHint }}
        </v-subheader>
      </v-layout>
    </v-slide-y-transition>
    <!-- Upper Pagination Controls -->
    <pagination-controls
      v-if="tvtemplatesPage"
      :show="showUpperPaginationControls"
      :page.sync="tvtemplatesPageIndex"
      :num-pages="tvtemplatesPage.num_pages"
      class="mt-2"
      hide-icons
    />
    <!-- TVTemplates Cards -->
    <v-container grid-list-lg>
      <template
        v-for="(
          tvtemplatesForCategoryId, categoryId
        ) in tvtemplatesGroupedByCategoryId"
      >
        <div
          v-if="categoryId"
          :key="categoryId"
          class="title mb-1 grey--text text--darken-3"
        >
          {{ categoryIdToName[categoryId] }}
        </div>
        <v-layout
          :key="'layout-category-' + categoryId"
          wrap
          class="mb-3"
        >
          <v-flex
            v-for="tvtemplate in tvtemplatesForCategoryId"
            :key="
              'tvtemplatepreview-' +
                tvtemplate.id +
                '-' +
                lastBrandingAppliedId +
                '-' +
                tvtemplatesPreviewRenderKey
            "
            xs12
            sm6
          >
            <v-card
              hover
              flat
              class="bordered--subtle"
              style="height: 100%; user-select: none"
              data-cy="tv-template-card"
              :title="tvtemplate.title"
              @click="
                $router.push({
                  name: 'tvtemplate',
                  params: {tvtemplateId: tvtemplate.id},
                })
              "
            >
              <v-layout
                column
                justify-center
                style="height: 100%"
              >
                <v-btn
                  icon
                  class="ml-auto"
                  style="position: absolute; right: 0; top: 0; z-index: 2"
                  :class="
                    tvtemplate.is_favorited_by_user
                      ? 'msr--text white'
                      : 'grey--text text--lighten-2 white'
                  "
                  :title="
                    tvtemplate.is_favorited_by_user
                      ? $t('tvtemplatesFavoriteUnset')
                      : $t('tvtemplatesFavoriteSet')
                  "
                  @click.stop="toggleFavorite(tvtemplate)"
                >
                  <v-icon>favorite</v-icon>
                </v-btn>
                <v-card-text>
                  <v-layout justify-center>
                    <tv-template-preview
                      v-if="tvtemplate.html_overlay"
                      :ref="'preview-' + tvtemplate.id"
                      class="pt-4 pb-2"
                      hide-player-controls
                      :html-overlay="tvtemplate.html_overlay"
                      :background-image="tvtemplate.video_poster_for_editing"
                      :width="tvtemplate.tvformat.width"
                      :height="tvtemplate.tvformat.height"
                      :max-width="tvtemplatePreviewMaxWidth"
                      :max-height="tvtemplatePreviewMaxHeight"
                      :is-multiscreen="
                        tvtemplate.tvformat.is_multiscreen_format
                      "
                      :onload-callback="
                        (vm) => populateIframeWithDefaultOverlayData(tvtemplate)
                      "
                    />
                  </v-layout>
                </v-card-text>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>
      </template>
    </v-container>
    <!-- Lower Pagination Controls -->
    <pagination-controls
      v-if="tvtemplatesPage"
      :show="showLowerPaginationControls"
      :page.sync="tvtemplatesPageIndex"
      :num-pages="tvtemplatesPage.num_pages"
      hide-icons
    />
  </div>
</template>

<script>
import {deepCopyObject} from '@/utils';

import {getDefaultOverlayDataForOverlayOptions} from '@/mixins/overlayOptions.js';
import {
  chooseDefaultBrandingFromBrandings,
  redirectToCreateBrandingPage,
} from '@/mixins/tvtemplateBrandings';

import AllTvTemplatesBrandingWidget from '@/components/AllTvTemplatesBrandingWidget.vue';
import ContentCreatorPlanStatusHint from '@/components/ContentCreatorPlanStatusHint.vue';
import Navbar from '@/components/Navbar';
import PaginationControls from '@/components/PaginationControls';
import TvTemplatePreview from '@/components/TvTemplatePreview.vue';
export default {
  components: {
    AllTvTemplatesBrandingWidget,
    ContentCreatorPlanStatusHint,
    Navbar,
    PaginationControls,
    TvTemplatePreview,
  },
  data() {
    return {
      links: ['dashboard', 'tvtemplates'],
      loading: false,
    };
  },
  computed: {
    noTvTemplatesHint() {
      if (
        this.filterFavorites &&
        this.formatFilterLandscape &&
        !this.formatFilterPortrait
      ) {
        return this.$t('noFavoritedTvTemplatesLandscape');
      } else if (
        this.filterFavorites &&
        !this.formatFilterLandscape &&
        this.formatFilterPortrait
      ) {
        return this.$t('noFavoritedTvTemplatesPortrait');
      } else if (this.filterFavorites) {
        return this.$t('noFavoritedTvTemplates');
      }
      return this.$t('noTvTemplates');
    },
    lastBrandingApplied: {
      get() {
        return this.$store.state.common.lastBrandingApplied;
      },
      set(branding) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      },
    },
    lastBrandingAppliedId() {
      if (!this.lastBrandingApplied) {
        return 0;
      }
      return this.lastBrandingApplied.id;
    },
    brandings() {
      return this.$store.state.common.brandings;
    },
    defaultBranding() {
      return chooseDefaultBrandingFromBrandings(this.brandings);
    },
    formatFilterLandscape: {
      get() {
        return this.$store.state.common.tvtemplatesFilterLandscape;
      },
      set(flag) {
        this.$store.commit('SET_TV_TEMPLATES_FILTER_LANDSCAPE', flag);
      },
    },
    formatFilterPortrait: {
      get() {
        return this.$store.state.common.tvtemplatesFilterPortrait;
      },
      set(flag) {
        this.$store.commit('SET_TV_TEMPLATES_FILTER_PORTRAIT', flag);
      },
    },
    filterFavorites: {
      get() {
        return this.$store.state.common.tvtemplatesFilterFavorites;
      },
      set(flag) {
        this.$store.commit('SET_TV_TEMPLATES_FILTER_FAVORITES', flag);
      },
    },
    tvtemplatesPreviewRenderKey() {
      return this.$store.state.common.tvtemplatesPreviewRenderKey;
    },
    tvtemplatesPage() {
      return this.$store.state.common.tvtemplatesPage;
    },
    tvtemplatesPageIndex: {
      get() {
        return this.$store.state.common.tvtemplatesPageIndex;
      },
      set(index) {
        this.$store.commit('SET_TV_TEMPLATES_PAGE_INDEX', index);
      },
    },
    tvtemplates() {
      if (!this.tvtemplatesPage) {
        return [];
      }
      return this.tvtemplatesPage.results;
    },
    categories() {
      const seenIds = [];
      const uniqueCategories = [];
      for (const tvtemplate of this.tvtemplates) {
        if (!tvtemplate.category) {
          continue;
        }
        if (seenIds.indexOf(tvtemplate.category.id) !== -1) {
          continue;
        }
        uniqueCategories.push(tvtemplate.category);
        seenIds.push(tvtemplate.category.id);
      }
      return uniqueCategories;
    },
    categoryIdToName() {
      const idToName = {};
      for (const category of this.categories) {
        idToName[category.id] = category.name;
      }
      return idToName;
    },
    tvtemplatesGroupedByCategoryId() {
      const categoryIdToTemplates = {};
      for (const tvtemplate of this.tvtemplates) {
        const categoryId = tvtemplate.category ? tvtemplate.category.id : null;
        const templates = categoryIdToTemplates[categoryId] || [];
        templates.push(tvtemplate);
        categoryIdToTemplates[categoryId] = templates;
      }
      return categoryIdToTemplates;
    },
    /**
     * Hand-tweak for lg and sm (two columns), otherwise use generic fallback.
     */
    tvtemplatePreviewMaxWidth() {
      const drawerWidth = 300;
      const padding = 96;

      if (this.$vuetify.breakpoint.lgAndUp) {
        let width = this.$vuetify.breakpoint.width / 2 - drawerWidth - padding;
        return width > 480 ? 480 : width;
      }
      // Two columns.
      if (this.$vuetify.breakpoint.md) {
        return this.$vuetify.breakpoint.width / 2 - drawerWidth;
      }
      // Less padding left/right.
      if (this.$vuetify.breakpoint.sm) {
        return this.$vuetify.breakpoint.width / 2 - padding;
      }
      // One column.
      return this.$vuetify.breakpoint.width - 120;
    },
    tvtemplatePreviewMaxHeight() {
      return 400;
    },
    showUpperPaginationControls() {
      return (
        this.tvtemplatesPage &&
        this.tvtemplatesPage.num_pages > 1 &&
        this.tvtemplates.length > 0
      );
    },
    showLowerPaginationControls() {
      return this.showUpperPaginationControls;
    },
  },
  watch: {
    formatFilterLandscape: function () {
      this.$store.commit('SET_TV_TEMPLATES_PAGE_INDEX', 1);
      this.fetchTVTemplatesPage();
    },
    formatFilterPortrait: function () {
      this.$store.commit('SET_TV_TEMPLATES_PAGE_INDEX', 1);
      this.fetchTVTemplatesPage();
    },
    filterFavorites: function () {
      this.$store.commit('SET_TV_TEMPLATES_PAGE_INDEX', 1);
      this.fetchTVTemplatesPage();
    },
    tvtemplatesPageIndex: function () {
      this.fetchTVTemplatesPage();
    },
  },
  methods: {
    fetchInitialDataAndHandlePotentialRedirect() {
      if (this.tvtemplates.length === 0 || this.brandings.length === 0) {
        this.loading = true;
      }
      this.$store.dispatch('LIST_BRANDINGS').then(() => {
        this.loading = false;

        if (this.$store.getters.showContentCreatorLandingPageItem) {
          this.$router.push({name: 'contentcreatorlanding'});
          return;
        }

        // HACK: For some reason, our this.brandings computed may lag
        //   behind here, so get it directly from the store.
        if (
          this.$store.state.common.brandings.length === 0 &&
          this.$store.getters.userCanUseBrandings
        ) {
          redirectToCreateBrandingPage(this, 'alert');
          return;
        }

        this.loading = true;
        this.fetchTVTemplatesPage().finally(() => {
          this.loading = false;
        });
      });
    },
    incrementTvTemplatesPreviewRenderKey() {
      this.$store.commit('INCREMENT_TV_TEMPLATES_PREVIEW_RENDERKEY');
    },
    injectLastAppliedOrDefaultBrandingIntoOverlayData(overlayData) {
      const actualBrandingToUse =
        this.lastBrandingApplied || this.defaultBranding;
      if (!actualBrandingToUse) {
        return;
      }
      overlayData.branding = deepCopyObject(actualBrandingToUse);
    },
    injectTvTemplateDurationIntoOverlayData(overlayData, tvtemplate) {
      overlayData.duration = tvtemplate.duration;
    },
    onBrandingAppliedToTvTemplate({branding, tvtemplate, changeset}) {
      this.lastBrandingApplied = branding;

      const overlayData = getDefaultOverlayDataForOverlayOptions(
        tvtemplate.overlay_options
      );
      // Override default fields by applying the changeset.
      for (const fieldName of Object.keys(changeset)) {
        overlayData[fieldName] = changeset[fieldName];
      }
      this.refreshIframeWithOverlayData(tvtemplate, overlayData);
    },
    onAllBrandingsHaveBeenApplied() {
      // We need to refetch the tvtemplates list based on the
      // lastBrandingApplied as some Brand-specific IMAGE default_values
      // may have changed.
      this.fetchTVTemplatesPage();
    },
    /**
     * Fetch current page of TVTemplates list with values from store.
     */
    fetchTVTemplatesPage({incrementKey = true} = {}) {
      if (incrementKey) {
        this.incrementTvTemplatesPreviewRenderKey();
      }
      return this.$store.dispatch('LIST_TV_TEMPLATES');
    },
    populateIframeWithDefaultOverlayData(tvtemplate) {
      const defaultOverlayData = getDefaultOverlayDataForOverlayOptions(
        tvtemplate.overlay_options
      );
      this.refreshIframeWithOverlayData(tvtemplate, defaultOverlayData);

      const actualBrandingToUse =
        this.lastBrandingApplied || this.defaultBranding;

      if (!!actualBrandingToUse) {
        // Apply the last/default Branding changeset on top.
        const widget = this.$refs.applybrandingwidget;
        if (!widget) {
          return;
        }
        const changeset = widget.getChangesetToApplyBranding(
          actualBrandingToUse,
          tvtemplate.overlay_options
        );
        this.onBrandingAppliedToTvTemplate({
          branding: actualBrandingToUse,
          tvtemplate: tvtemplate,
          changeset: changeset,
        });
      }
    },
    refreshIframeWithOverlayData(tvtemplate, overlayData) {
      this.injectLastAppliedOrDefaultBrandingIntoOverlayData(overlayData);
      this.injectTvTemplateDurationIntoOverlayData(overlayData, tvtemplate);

      this.$nextTick(() => {
        const previewWidgetRefs = this.$refs['preview-' + tvtemplate.id];
        if (previewWidgetRefs) {
          // It is a list, because we used ref within a v-for loop.
          const widget = previewWidgetRefs[0];
          if (widget) {
            widget.updateOverlayData(overlayData);
          }
        }
      });
    },
    toggleFavorite(tvtemplate) {
      this.$api
        .updateTVTemplateFavorited(
          tvtemplate.id,
          !tvtemplate.is_favorited_by_user
        )
        .then(() => {
          this.$store.commit('SET_TV_TEMPLATES_PAGE_INDEX', 1);
          this.fetchTVTemplatesPage();
        });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.incrementTvTemplatesPreviewRenderKey();
      vm.fetchInitialDataAndHandlePotentialRedirect();
    });
  },
};
</script>

<style scoped>
.typefilter-menu {
  min-width: 160px;
}

.v-menu__content >>> .no-scrollbar {
  overflow-y: hidden;
}

.no-padding >>> .v-list__tile {
  padding: 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/TvTemplates.vue